import { C, RADIUS, SHADOW, soft } from '@parsina/framework/tokens';
import { STACK } from '../constants/density';

/**
 * One record, as a card.
 *
 * A row is a desktop control. The clinic's tables run to seven and eight
 * columns, and at 360px any of them forces the PAGE to scroll sideways, which
 * this design forbids. So on the phone a record is a card: fewer facts, chosen
 * by what the operator acts on, at a size a thumb can hit.
 *
 * Density inverts here on purpose. `ROW_PAD.compact` ('8px 12px') is the right
 * answer on a wide fixed monitor where every row below the fold is a scroll
 * paid dozens of times a shift. On a phone the scarce resource is not vertical
 * pixels but thumb accuracy, so the card pads to 44px-friendly values while
 * still taking its rhythm from the shared scale rather than hand-picked
 * numbers. See docs/MOBILE-REDESIGN.md §2.2.
 *
 * The status marker rides INSIDE the card as an inset shadow rather than as a
 * border, so it cannot change the card's box and misalign it against its
 * neighbours — the alignment bug the desktop table solved with a negative
 * margin on the first cell.
 */
export default function MobileCard({
  children,
  onClick,
  /** Names the whole-card tap for assistive tech. Required with `onClick`. */
  label,
  /** `true` when the tap navigates, so assistive tech announces a link. */
  navigates = false,
  /** A token colour for the inset status marker, or null for none. */
  marker = null,
  /** Dims a card whose record is finished or inactive. */
  muted = false,
  /** Tints the whole card, for a record that needs to be seen at a glance. */
  tone = null,
  style,
  'data-testid': testId,
  'data-state': dataState,
  ...rest
}) {
  const interactive = Boolean(onClick);

  const base = {
    position: 'relative',
    display: 'block',
    width: '100%',
    textAlign: 'start',
    background: tone || C.card,
    border: `1px solid ${C.border}`,
    borderRadius: RADIUS.card,
    boxShadow: marker ? `${SHADOW.card}, inset 3px 0 0 ${marker}` : SHADOW.card,
    padding: '0.75rem 0.875rem',
    minHeight: '2.75rem',
    opacity: muted ? 0.6 : 1,
    font: 'inherit',
    color: 'inherit',
    ...style,
  };

  return (
    <div style={base} data-testid={testId} data-state={dataState} {...rest}>
      {/*
        THE WHOLE-CARD TAP IS AN OVERLAY, NOT A WRAPPER.

        Almost every card in this app is both "tap to open the record" AND
        carries its own actions — a primary button, a row menu. Making the card
        itself a <button> nests those inside it, which is invalid HTML: the
        parser unwraps the inner control, so the nested button lands OUTSIDE
        the card in the DOM, and browsers disagree about which of the two a tap
        fires. It also announces as one enormous control with the actions'
        labels swallowed into its accessible name.

        So the tap target is a sibling that covers the card, and anything
        interactive is raised above it by `MobileCardActions` / the row menu's
        own stacking. One control per behaviour, no nesting, and tapping the
        body still opens the record.
      */}
      {interactive && (
        <button
          type="button"
          role={navigates ? 'link' : undefined}
          onClick={onClick}
          aria-label={label}
          data-testid={testId ? `${testId}-open` : undefined}
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            width: '100%',
            background: 'transparent',
            border: 'none',
            borderRadius: RADIUS.card,
            cursor: 'pointer',
            font: 'inherit',
          }}
        />
      )}
      {children}
    </div>
  );
}

/** The card's own title row: identity on the start edge, status on the end. */
export function MobileCardHead({ children, style }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '0.5rem',
      minWidth: 0, ...style,
    }}>
      {children}
    </div>
  );
}

/** A muted secondary line. Missing parts are omitted, never rendered as «—». */
export function MobileCardMeta({ parts = [], style }) {
  const shown = parts.filter(Boolean);
  if (!shown.length) return null;
  return (
    <div
      dir="auto"
      style={{
        fontSize: '0.6875rem', color: C.textMuted,
        marginTop: '0.25rem',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {shown.reduce((acc, p, i) => (i === 0 ? [p] : [...acc, ' · ', p]), [])}
    </div>
  );
}

/**
 * The card's action shelf: a full-width primary, separated by a hairline.
 *
 * `position: relative; z-index: 2` is load-bearing, not decoration. The
 * card's whole-body tap is an absolutely-positioned overlay at `z-index: 1`
 * (see `MobileCard`); anything meant to be independently pressable has to sit
 * above it, or the overlay swallows the press and every action on every card
 * silently opens the record instead.
 */
export function MobileCardActions({ children, style }) {
  return (
    <div style={{
      position: 'relative',
      zIndex: 2,
      display: 'flex', gap: `${STACK.tight}px`, alignItems: 'center',
      marginTop: '0.625rem', paddingTop: '0.625rem',
      borderTop: `1px solid ${soft(C.border, 1)}`,
      ...style,
    }}>
      {children}
    </div>
  );
}
